import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, GitCompare, RefreshCw, Trophy, Clock, Eye, CheckCircle2, Lightbulb, X, Shuffle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_QUESTIONS = 5;
const MAX_QUESTIONS = 8;

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function Matching() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const topics = searchParams.get('topics') || '';
  const difficulties = searchParams.get('difficulties') || '';
  const subtopics = searchParams.get('subtopics') || '';

  const [allQuestions, setAllQuestions] = useState([]);  // Full pool of questions
  const [questions, setQuestions] = useState([]);         // Current round questions
  const [leftItems, setLeftItems] = useState([]);         // Prompts
  const [rightItems, setRightItems] = useState([]);       // Answers
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [matched, setMatched] = useState(new Set());
  const [revealed, setRevealed] = useState(new Set());    // Track revealed (Show Answer) items
  const [incorrect, setIncorrect] = useState({ left: null, right: null });
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [attempts, setAttempts] = useState(0);
  
  // Session-based tracking to avoid repeats
  const usedQuestionIds = useRef(new Set());
  const [poolExhausted, setPoolExhausted] = useState(false);
  const [lowQuestionWarning, setLowQuestionWarning] = useState(null);
  
  // Rationale display state
  const [showRationale, setShowRationale] = useState(null);  // question_id to show rationale for
  const [rationaleText, setRationaleText] = useState('');
  const [sessionSaved, setSessionSaved] = useState(false);

  // Initialize a round with available questions (excluding used ones)
  const initializeRound = useCallback((pool, usedIds) => {
    // Filter out already used questions
    const available = pool.filter(q => !usedIds.has(q.question_id));
    
    if (available.length < MIN_QUESTIONS) {
      // Pool exhausted or not enough questions
      if (available.length > 0) {
        setLowQuestionWarning(`Only ${available.length} questions remaining. Using all available.`);
        setupGameBoard(available);
      } else {
        setPoolExhausted(true);
      }
      return;
    }
    
    // Select MIN to MAX questions for this round
    const roundSize = Math.min(MAX_QUESTIONS, available.length);
    const shuffled = shuffleArray(available);
    const roundQuestions = shuffled.slice(0, roundSize);
    
    setupGameBoard(roundQuestions);
    setLowQuestionWarning(null);
    setPoolExhausted(false);
  }, []);

  // Setup the game board with selected questions
  const setupGameBoard = (roundQuestions) => {
    setQuestions(roundQuestions);
    
    // Mark these questions as used
    roundQuestions.forEach(q => usedQuestionIds.current.add(q.question_id));
    
    // Create shuffled left (prompts) and right (answers) columns
    setLeftItems(shuffleArray(roundQuestions.map(q => ({ 
      id: q.question_id, 
      text: q.prompt 
    }))));
    setRightItems(shuffleArray(roundQuestions.map(q => ({ 
      id: q.question_id, 
      text: q.correct_answer.length > 25 ? q.correct_answer.slice(0, 25) + '...' : q.correct_answer,
      fullAnswer: q.correct_answer
    }))));
    
    // Reset game state
    setMatched(new Set());
    setRevealed(new Set());
    setSelectedLeft(null);
    setSelectedRight(null);
    setIncorrect({ left: null, right: null });
    setAttempts(0);
    setShowRationale(null);
    setSessionSaved(false);
    setStartTime(Date.now());
    setElapsed(0);
  };

  const fetchQuestions = useCallback(async () => {
    try {
      const params = {
        game_type: 'matching',
        topics: topics || undefined,
        difficulties: difficulties || undefined,
        subtopics: subtopics || undefined,
        limit: 50  // Fetch larger pool for session tracking
      };
      const res = await axios.get(`${BACKEND_URL}/api/games/questions`, { params, withCredentials: true });
      const gameQuestions = res.data.questions || [];
      
      if (gameQuestions.length < MIN_QUESTIONS) {
        if (gameQuestions.length > 0) {
          setLowQuestionWarning(`Only ${gameQuestions.length} matching questions available. Need at least ${MIN_QUESTIONS} for a full set.`);
        } else {
          toast.error('Not enough matching questions available');
        }
        setLoading(false);
        setAllQuestions(gameQuestions);
        if (gameQuestions.length > 0) {
          initializeRound(gameQuestions, new Set());
        }
        return;
      }
      
      setAllQuestions(gameQuestions);
      initializeRound(gameQuestions, new Set());
    } catch (err) {
      toast.error('Failed to load game');
    } finally {
      setLoading(false);
    }
  }, [topics, difficulties, subtopics, initializeRound]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    if (startTime && matched.size + revealed.size < questions.length) {
      const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
      return () => clearInterval(timer);
    }
  }, [startTime, matched.size, revealed.size, questions.length]);

  // Save session when complete
  const saveGameSession = useCallback(async () => {
    if (sessionSaved || questions.length === 0) return;
    try {
      await axios.post(`${BACKEND_URL}/api/games/session`, {
        game_type: 'matching',
        topic_key: topics || null,
        difficulty: difficulties || null,
        completed_items: matched.size,
        revealed_items: revealed.size,
        total_items: questions.length,
        correct_items: matched.size,
        time_seconds: elapsed
      }, { withCredentials: true });
      setSessionSaved(true);
    } catch (err) {
      console.error('Failed to save session');
    }
  }, [topics, difficulties, matched.size, revealed.size, questions.length, elapsed, sessionSaved]);

  useEffect(() => {
    if (matched.size + revealed.size === questions.length && questions.length > 0 && !sessionSaved) {
      saveGameSession();
    }
  }, [matched.size, revealed.size, questions.length, saveGameSession, sessionSaved]);

  // Handle match checking
  useEffect(() => {
    if (selectedLeft !== null && selectedRight !== null) {
      setAttempts(a => a + 1);
      
      if (selectedLeft === selectedRight) {
        // Correct match!
        const q = questions.find(q => q.question_id === selectedLeft);
        setMatched(prev => new Set([...prev, selectedLeft]));
        setShowRationale(selectedLeft);
        setRationaleText(q?.rationale || '');
        setSelectedLeft(null);
        setSelectedRight(null);
      } else {
        // Incorrect match
        setIncorrect({ left: selectedLeft, right: selectedRight });
        setTimeout(() => {
          setIncorrect({ left: null, right: null });
          setSelectedLeft(null);
          setSelectedRight(null);
        }, 800);
      }
    }
  }, [selectedLeft, selectedRight, questions]);

  const handleShowAnswer = (questionId) => {
    const q = questions.find(q => q.question_id === questionId);
    if (!q) return;
    
    // Mark as revealed (not completed)
    setRevealed(prev => new Set([...prev, questionId]));
    setShowRationale(questionId);
    setRationaleText(q.rationale || '');
    
    // Clear any selection
    setSelectedLeft(null);
    setSelectedRight(null);
  };

  const dismissRationale = () => {
    setShowRationale(null);
    setRationaleText('');
  };

  const handleRestart = () => {
    // Restart current set (reshuffles same questions)
    setMatched(new Set());
    setRevealed(new Set());
    setSelectedLeft(null);
    setSelectedRight(null);
    setIncorrect({ left: null, right: null });
    setAttempts(0);
    setShowRationale(null);
    setSessionSaved(false);
    setLeftItems(shuffleArray(questions.map(q => ({ id: q.question_id, text: q.prompt }))));
    setRightItems(shuffleArray(questions.map(q => ({ 
      id: q.question_id, 
      text: q.correct_answer.length > 25 ? q.correct_answer.slice(0, 25) + '...' : q.correct_answer,
      fullAnswer: q.correct_answer
    }))));
    setStartTime(Date.now());
    setElapsed(0);
  };

  // Reset entire session (clear used tracking)
  const handleResetSession = () => {
    usedQuestionIds.current = new Set();
    setPoolExhausted(false);
    initializeRound(allQuestions, new Set());
    toast.success('Session reset! All questions available again.');
  };

  // Handle "Shuffle / New Set" button
  const handleNewSet = () => {
    if (poolExhausted) {
      // Reset session tracking and start fresh
      usedQuestionIds.current = new Set();
      initializeRound(allQuestions, new Set());
      toast.success('Pool reset! Starting fresh.');
    } else {
      initializeRound(allQuestions, usedQuestionIds.current);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const isItemDone = (id) => matched.has(id) || revealed.has(id);
  const isComplete = matched.size + revealed.size === questions.length && questions.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (questions.length === 0 && !poolExhausted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <GitCompare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Not Enough Questions</h2>
          <p className="text-slate-600 mb-4">Need at least {MIN_QUESTIONS} matching game questions</p>
          <Button onClick={() => navigate('/games/matching/setup')}>Change Filters</Button>
        </div>
      </div>
    );
  }

  // Pool exhausted state
  if (poolExhausted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-background flex items-center justify-center">
        <div className="text-center bg-white rounded-xl border border-slate-200 p-8 max-w-md">
          <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">All Questions Completed!</h2>
          <p className="text-slate-600 mb-6">You&apos;ve gone through all available matching questions for this topic.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleResetSession} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" /> Start Over
            </Button>
            <Button onClick={() => navigate('/games')} className="bg-purple-500 text-white">
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button onClick={() => navigate('/games/matching/setup')} variant="ghost" className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-2" /> Exit
            </Button>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span className="font-mono">{formatTime(elapsed)}</span>
              </div>
              <div className="flex items-center gap-2">
                <GitCompare className="w-5 h-5 text-purple-500" />
                <span className="font-semibold text-slate-900">Matching</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleNewSet} variant="ghost" className="text-purple-600 hover:bg-purple-50">
                <Shuffle className="w-4 h-4 mr-2" /> New Set
              </Button>
              <Button onClick={handleRestart} variant="ghost" className="text-slate-600">
                <RefreshCw className="w-4 h-4 mr-2" /> Restart
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Low question warning */}
        {lowQuestionWarning && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800">{lowQuestionWarning}</span>
          </div>
        )}
        
        {/* Progress */}
        <div className="text-center mb-6">
          <p className="text-slate-600">
            Completed: {matched.size} | Revealed: {revealed.size} | Remaining: {questions.length - matched.size - revealed.size}
          </p>
          <div className="w-full h-2 bg-purple-100 rounded-full mt-2">
            <div 
              className="h-full bg-purple-500 rounded-full transition-all" 
              style={{ width: `${((matched.size + revealed.size) / questions.length) * 100}%` }} 
            />
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Questions used this session: {usedQuestionIds.current.size} / {allQuestions.length}
          </p>
        </div>

        {/* Game Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Prompts */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Clinical Prompts</h3>
            {leftItems.map(item => {
              const isDone = isItemDone(item.id);
              const isSelected = selectedLeft === item.id;
              const isIncorrect = incorrect.left === item.id;
              const isMatched = matched.has(item.id);
              const isRevealed = revealed.has(item.id);
              
              return (
                <div key={`left-${item.id}`} className="relative">
                  <button
                    onClick={() => !isDone && setSelectedLeft(item.id)}
                    disabled={isDone || showRationale !== null}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      isMatched ? 'bg-green-50 border-green-300 opacity-70' :
                      isRevealed ? 'bg-amber-50 border-amber-300 opacity-70' :
                      isIncorrect ? 'bg-red-50 border-red-300' :
                      isSelected ? 'bg-purple-50 border-purple-500 shadow-md' :
                      'bg-white border-slate-200 hover:border-purple-300 hover:shadow'
                    }`}
                  >
                    <span className={`text-sm ${isDone ? 'text-slate-500' : 'text-slate-900'}`}>
                      {item.text}
                    </span>
                    {isMatched && <CheckCircle2 className="absolute right-3 top-3 w-4 h-4 text-green-600" />}
                    {isRevealed && <Eye className="absolute right-3 top-3 w-4 h-4 text-amber-600" />}
                  </button>
                  
                  {/* Show Answer button */}
                  {!isDone && (
                    <button
                      onClick={() => handleShowAnswer(item.id)}
                      className="absolute -right-2 -top-2 p-1.5 bg-white border border-slate-200 rounded-full shadow-sm hover:bg-slate-50 text-slate-400 hover:text-amber-600"
                      title="Show Answer"
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right Column - Answers */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Answers</h3>
            {rightItems.map(item => {
              const isDone = isItemDone(item.id);
              const isSelected = selectedRight === item.id;
              const isIncorrect = incorrect.right === item.id;
              const isMatched = matched.has(item.id);
              const isRevealed = revealed.has(item.id);
              
              return (
                <button
                  key={`right-${item.id}`}
                  onClick={() => !isDone && setSelectedRight(item.id)}
                  disabled={isDone || showRationale !== null}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    isMatched ? 'bg-green-50 border-green-300 opacity-70' :
                    isRevealed ? 'bg-amber-50 border-amber-300 opacity-70' :
                    isIncorrect ? 'bg-red-50 border-red-300' :
                    isSelected ? 'bg-purple-50 border-purple-500 shadow-md' :
                    'bg-white border-slate-200 hover:border-purple-300 hover:shadow'
                  }`}
                >
                  <span className={`text-sm font-medium ${isDone ? 'text-slate-500' : 'text-slate-900'}`}>
                    {isDone ? item.fullAnswer : item.text}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Rationale Modal */}
        {showRationale && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {matched.has(showRationale) ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : (
                    <Eye className="w-6 h-6 text-amber-600" />
                  )}
                  <span className="font-semibold text-slate-900">
                    {matched.has(showRationale) ? 'Correct Match!' : 'Answer Revealed'}
                  </span>
                </div>
                <button onClick={dismissRationale} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wide text-purple-600 mb-1">Prompt</p>
                  <p className="text-slate-900">{questions.find(q => q.question_id === showRationale)?.prompt}</p>
                </div>
                
                <div className={`rounded-lg p-4 ${matched.has(showRationale) ? 'bg-green-50' : 'bg-amber-50'}`}>
                  <p className={`text-xs uppercase tracking-wide mb-1 ${matched.has(showRationale) ? 'text-green-600' : 'text-amber-600'}`}>
                    Answer
                  </p>
                  <p className="font-semibold text-slate-900">
                    {questions.find(q => q.question_id === showRationale)?.correct_answer}
                  </p>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    <p className="text-xs uppercase tracking-wide text-slate-600">Rationale</p>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{rationaleText}</p>
                </div>
              </div>
              
              <Button onClick={dismissRationale} className="w-full mt-6 bg-purple-500 hover:bg-purple-600 text-white">
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Completion */}
        {isComplete && !showRationale && (
          <div className="mt-8 bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Round Complete!</h3>
            <div className="space-y-1 text-slate-600 mb-4">
              <p>Time: {formatTime(elapsed)}</p>
              <p>Matched correctly: <span className="text-green-600 font-semibold">{matched.size}</span></p>
              <p>Revealed (Show Answer): <span className="text-amber-600 font-semibold">{revealed.size}</span></p>
              <p>Total attempts: {attempts}</p>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {allQuestions.length - usedQuestionIds.current.size > 0 
                ? `${allQuestions.length - usedQuestionIds.current.size} more questions available`
                : 'All questions in pool completed!'
              }
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              {allQuestions.length - usedQuestionIds.current.size > 0 && (
                <Button onClick={handleNewSet} className="bg-purple-500 hover:bg-purple-600 text-white">
                  <Shuffle className="w-4 h-4 mr-2" /> New Set
                </Button>
              )}
              <Button onClick={handleRestart} variant="outline">Play Again (Same Set)</Button>
              <Button onClick={() => navigate('/games')} variant="outline">Back to Games</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
